import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import {
  Body,
  Content,
  Footer,
  List,
  Row,
  TitleBlock,
} from "../recentlyDeletedStyles";
import { Text } from "../Text";
import { useToast } from "../Toast";
import { chatLabel, pickChatIconColor } from "./pickChatIconColor";

export function RecentlyDeletedChatsModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const z = useZero();
  const { showToast } = useToast();
  const [deletedChats, result] = useQuery(queries.deletedPersonalChats());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [restoring, setRestoring] = useState<typeof deletedChats | null>(null);
  const pending = restoring !== null;
  const chats = restoring ?? deletedChats;
  const selectedIds = chats
    .filter((chat) => selected.has(chat.id))
    .map((chat) => chat.id);

  const close = () => {
    if (!pending) onClose();
  };

  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const restore = async () => {
    if (pending || selectedIds.length === 0) return;
    setRestoring(chats);
    const results = await Promise.allSettled(
      selectedIds.map(
        async (id) =>
          await z.mutate(sharedMutators.restorePersonalChat({ id })).server
      )
    );
    const failedIds = selectedIds.filter((_, index) => {
      const result = results[index]!;
      return result.status === "rejected" || result.value.type === "error";
    });
    setRestoring(null);
    if (failedIds.length > 0) {
      setSelected(new Set(failedIds));
      showToast({
        icon: "circle-off",
        message: "Some chats couldn’t be restored. Please try again.",
        buttonLabel: "Dismiss",
      });
      return;
    }
    onClose();
  };

  return (
    <Modal open onClose={close}>
      <Content>
        <TitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Deleted chats
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            {chats.length > 0
              ? "Restore deleted chats to your chat history."
              : result.type === "complete"
                ? "No deleted chats."
                : "Loading deleted chats…"}
          </Text>
        </TitleBlock>
        <Body>
          {chats.length > 0 && (
            <List>
              {chats.map((chat) => {
                const isSelected = selected.has(chat.id);
                return (
                  <Row
                    key={chat.id}
                    data-no-pill=""
                    onPress={() => toggle(chat.id)}
                    aria-pressed={isSelected}
                    disabled={pending}
                    title={chatLabel(chat.name)}
                  >
                    <Icon
                      name="ask"
                      size={16}
                      color={`var(--${pickChatIconColor(chat.id)}-600)`}
                      style={{ flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        variant="text-regular"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {chatLabel(chat.name)}
                      </Text>
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        opacity: isSelected ? 1 : 0.12,
                      }}
                    >
                      <Icon
                        name={isSelected ? "check-filled" : "check-empty"}
                        size={16}
                      />
                    </span>
                  </Row>
                );
              })}
            </List>
          )}
          <Footer>
            <Button
              size="l"
              variant="subtle"
              onClick={close}
              disabled={pending}
            >
              {chats.length > 0 ? "Cancel" : "Done"}
            </Button>
            {chats.length > 0 && (
              <Button
                size="l"
                variant="primary"
                onClick={restore}
                disabled={pending || selectedIds.length === 0}
                aria-busy={pending}
              >
                Restore
              </Button>
            )}
          </Footer>
        </Body>
      </Content>
    </Modal>
  );
}
